import React, { useEffect, useState } from 'react';

import {
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  Title,
  createStyles,
  px,
  useMantineTheme,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import CardInfoLine from 'components/InfoCard/CardInfoLine';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCreateAthleteClasses from 'hooks/athleteClass/useCreateAthleteClasses';
import useClasses from 'hooks/class/useClasses';
import { CellProps } from 'react-table';
import { AthleteKiloClassWithAthlete, DayAbbreviation, KiloClassListView, KiloClassRetrieve } from 'types';
import { getDayFromDate } from 'utils/event.helpers';
import notificationHandler from 'utils/notifications';

const useStyles = createStyles(theme => ({
  classInfo: {
    display: 'grid',
    rowGap: px(theme.spacing.xs) / 2,
    columnGap: theme.spacing.sm,
    alignItems: 'center',
    gridTemplateColumns: '100px auto',
    gridTemplateRows: 'auto',
    whiteSpace: 'nowrap',
    background: theme.colors.gray[2],
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
  },
}));

interface IModalAthleteClassCreateBulk {
  opened: boolean;
  handleClose: () => void;
  athleteKiloClass: AthleteKiloClassWithAthlete;
  kiloClass: KiloClassRetrieve;
}

interface FormValues {
  startDate: Date;
  endDate: Date;
  days: DayAbbreviation[];
}

const ModalAthleteClassCreateBulk = ({
  opened,
  handleClose,
  athleteKiloClass,
  kiloClass,
}: IModalAthleteClassCreateBulk): JSX.Element => {
  const { dateFormat } = useLocalization();

  const today = new Date();
  const oneYearFromToday = new Date(today);
  oneYearFromToday.setFullYear(oneYearFromToday.getFullYear() + 1);

  const form = useForm<FormValues>({
    initialValues: {
      startDate: today,
      endDate: oneYearFromToday,
      days: [],
    },
  });
  const [step, setStep] = useState<0 | 1>(0);
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const [classesTableData, setClassesTableData] = useState<KiloClassListView[]>([]);
  const createMutation = useCreateAthleteClasses();

  const {
    data: kiloClasses,
    isLoading: kiloClassesLoading,
    isSuccess: kiloClassesSuccess,
    isError: kiloClassesError,
  } = useClasses({
    startDate: dayjs(form.values.startDate).format('YYYY-MM-DD'),
    endDate: dayjs(form.values.endDate).format('YYYY-MM-DD'),
    days: form.values.days,
    enabled: !!kiloClass.kiloClassType.kiloClassTypeId && step == 1,
    startTime: kiloClass.startTime,
    kiloClassType: kiloClass.kiloClassType.kiloClassTypeId,
    location: kiloClass.location ? kiloClass.location.locationId : null,
    excludeRoot: true,
  });

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
    setStep(0);
  };

  const columns = [
    {
      Header: 'Weekday',
      Cell: ({ row }: CellProps<KiloClassListView>) => getDayFromDate(row.original.startDate),
    },
    {
      Header: 'Date',
      accessor: 'startDate',
      Cell: ({ value }: CellProps<KiloClassListView>) => dayjs(value).format(dateFormat),
    },
    {
      Header: 'Class Size',
      Cell: ({ row }: CellProps<KiloClassListView>) =>
        `${
          row.original.athleteKiloClasses.map((akc: AthleteKiloClassWithAthlete) => akc.isReserved).length
        }/${row.original.capacity}`,
    },
  ];

  useEffect(() => {
    if (!kiloClassesLoading && kiloClasses) {
      setClassesTableData(kiloClasses.data.flat());
    }
  }, [kiloClassesLoading, kiloClasses]);

  const { successNotification, errorNotification } = notificationHandler();
  const handleCreate = (): void => {
    if (!!kiloClass.kiloClassType) {
      createMutation.mutate(
        {
          athlete: athleteKiloClass.athlete.athleteId,
          startDate: dayjs(form.values.startDate).format('YYYY-MM-DD'),
          endDate: dayjs(form.values.endDate).format('YYYY-MM-DD'),
          days: form.values.days.join(','),
          startTime: kiloClass.startTime,
          kiloClassType: kiloClass.kiloClassType.kiloClassTypeId,
          location: kiloClass.location?.locationId ?? null,
        },
        {
          onSuccess: () => {
            successNotification(
              'Athlete reservations create success',
              'If credits are available, they will be assigned later today, starting with the earliest upcoming reservations and continuing chronologically into the future.',
            );
            handleClose();
          },
          onError: error => {
            errorNotification('Athlete reservations create failed', error.response?.data.message);
          },
        },
      );
    }
  };
  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={<Title order={4}>Bulk Reservations</Title>}
      size='md'
      sx={{
        '.mantine-Modal-body': { padding: 0 },
      }}
    >
      <Stack spacing={0}>
        <Divider />
        <div className={classes.classInfo}>
          <CardInfoLine label='Class Name' description={kiloClass.kiloClassType.name} />
          <CardInfoLine label='Athlete' description={athleteKiloClass.athlete.fullName} />
          <CardInfoLine label='Location' description={kiloClass.location?.name || '-'} />
        </div>
        <Divider />
        {step === 0 && (
          <>
            <Group pt='lg' pb='2.5rem' px='2rem' position='apart'>
              <Stack spacing='xs'>
                <Text fs='italic' c='dimmed' fz='sm'>
                  Time
                </Text>
                <Text fw='bold'>
                  {dayjs(`${kiloClass.startDate} ${kiloClass.startTime}`).format('h:mm A')}
                </Text>
              </Stack>
              <Stack spacing='xs'>
                <Text fs='italic' c='dimmed' fz='sm'>
                  Day of Week
                </Text>
                <Checkbox.Group {...form.getInputProps('days')}>
                  <Group noWrap spacing='lg'>
                    <Checkbox value='1' indeterminate icon={() => <>Sun</>} />
                    <Checkbox value='2' indeterminate icon={() => <>Mon</>} />
                    <Checkbox value='3' indeterminate icon={() => <>Tue</>} />
                    <Checkbox value='4' indeterminate icon={() => <>Wed</>} />
                    <Checkbox value='5' indeterminate icon={() => <>Thu</>} />
                    <Checkbox value='6' indeterminate icon={() => <>Fri</>} />
                    <Checkbox value='7' indeterminate icon={() => <>Sat</>} />
                  </Group>
                </Checkbox.Group>
              </Stack>
            </Group>
            <Divider />
            <Stack align='center' p='xl'>
              <Group>
                <Text fs='italic' c='dimmed' fz='xs'>
                  Start Date
                </Text>
                <DatePickerInput
                  {...form.getInputProps('startDate')}
                  minDate={today}
                  popoverProps={{ zIndex: 10000000, withinPortal: true }}
                  w={px(theme.spacing.xl) * 7}
                  {...{ placeholder: 'Select date' }}
                />
              </Group>
              <Group>
                <Text fs='italic' c='dimmed' fz='xs'>
                  End Date
                </Text>
                <DatePickerInput
                  {...form.getInputProps('endDate')}
                  popoverProps={{ zIndex: 10000000, withinPortal: true }}
                  w={px(theme.spacing.xl) * 7}
                  maxDate={oneYearFromToday}
                  {...{ placeholder: 'Select date' }}
                />
              </Group>
            </Stack>
            <Divider />
            <Group position='right' p='sm'>
              <Button variant='subtle' onClick={handleCloseAndReset}>
                Cancel
              </Button>
              <Button
                disabled={!form.values.days.length}
                py='sm'
                onClick={() => {
                  setStep(1);
                }}
              >
                Next
              </Button>
            </Group>
          </>
        )}
        {step === 1 && (
          <>
            <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
              <Table
                tableData={classesTableData}
                columns={columns}
                isSuccess={kiloClassesSuccess}
                isLoading={kiloClassesLoading}
                isError={kiloClassesError}
              />
            </div>
            <Group position='right' p='sm'>
              <Button variant='subtle' onClick={handleCloseAndReset}>
                Cancel
              </Button>
              <Button
                disabled={kiloClassesLoading || createMutation.isLoading}
                onClick={() => {
                  setStep(0);
                }}
              >
                Back
              </Button>
              <Button
                disabled={kiloClassesLoading}
                onClick={handleCreate}
                loading={createMutation.isLoading}
                color='green'
              >
                Confirm
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default ModalAthleteClassCreateBulk;
